import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { nombreDe, ruta } from '@/api/ruta'
import { api, ErrorAPI } from '@/api/cliente'
import { saltosDe, type Capacidad, type ClaveDelGuion, type Config, type MensajeDePlataforma, type Paso } from '@/tipos'
import { esLider, puedeEn, PERMISO_PUBLICAR } from '@/tipos/alcance'

// El estado del guion que se esta editando.
//
// Todo pasa por aqui para que el autoguardado, la validacion y el panel de
// prueba miren siempre lo mismo. Un editor donde el panel de prueba corre contra
// una copia distinta de la que se ve en pantalla es peor que no tener panel.

export const usarGuion = defineStore('guion', () => {
  const tenant      = ref('')
  const config      = ref<Config | null>(null)
  const baseVersion = ref(0)
  const catalogo    = ref<Capacidad[]>([])

  // De que capa sale cada mensaje. Vacio cuando este cliente todavia no tiene
  // su guion en capas: la pantalla sigue funcionando igual que antes, solo que
  // sin poder decir que es propio y que es compartido.
  const capasPlantilla = ref<Record<string, ClaveDelGuion>>({})
  const enCapas = ref(false)

  // Los mensajes que dice el motor, con el texto que sale si este cliente no
  // escribe el suyo.
  //
  // Se piden aparte porque mientras nadie los escriba NO estan en el guion: el
  // texto de plataforma no es una plantilla del cliente, es lo que el motor
  // pone al armar el bot. Sin esta lista la pantalla solo podria enseñarlos
  // despues de que alguien los escribiera — o sea, nunca, porque para
  // escribirlos hay que saber que existen.
  const mensajesPlataforma = ref<MensajeDePlataforma[]>([])

  const cargando  = ref(false)
  const guardando = ref(false)
  const sucio     = ref(false)
  const problemas = ref<string[]>([])
  const error     = ref('')

  const pasos = computed(() => Object.entries(config.value?.flujo?.pasos ?? {}))
  const nombresPasos = computed(() => pasos.value.map(([n]) => n).sort())
  const plantillas = computed(() => Object.entries(config.value?.plantillas ?? {}))

  // Las capacidades que este tenant tiene habilitadas, con su ficha del
  // catalogo. Es lo que alimenta el desplegable del paso de tipo capacidad.
  const capacidadesDelTenant = computed(() =>
    catalogo.value.filter(c => config.value?.capacidades?.includes(c.id)))

  async function abrir(id: string) {
    cargando.value = true
    error.value = ''
    try {
      // Los tres a la vez. Los mensajes de plataforma van aqui y no en una
      // carga aparte porque los sirve este mismo binario: si esa ruta falla, la
      // del catalogo tampoco esta, y esconder el fallo dejaria la pantalla a
      // medias sin decir por que.
      const [b, cat, mp] = await Promise.all([
        api.borrador(id), api.catalogo(), api.mensajesDePlataforma(),
      ])
      tenant.value = id
      config.value = b.config
      baseVersion.value = b.base_version ?? 0
      catalogo.value = cat.capacidades
      mensajesPlataforma.value = mp.mensajes
      sucio.value = false
      await cargarCapas()
      await validar()
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      cargando.value = false
    }
  }

  async function validar() {
    if (!config.value) return
    try {
      const r = await api.validar(tenant.value, config.value)
      problemas.value = r.problemas ?? []
    } catch { /* validar es informativo; su fallo no debe bloquear la edicion */ }
  }

  async function guardar(nota = '') {
    if (!config.value) return
    guardando.value = true
    try {
      const r = await api.guardarBorrador(tenant.value, config.value, baseVersion.value, nota)
      problemas.value = r.problemas ?? []
      sucio.value = false
      error.value = ''
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      guardando.value = false
    }
  }

  // Autoguardado. Guardar SIEMPRE se permite aunque el guion tenga problemas: a
  // media edicion esta roto por definicion, y perder el trabajo por eso seria
  // absurdo. Lo que no se permite es PUBLICAR con problemas.
  let temporizador: number | undefined
  watch(config, () => {
    if (cargando.value) return
    sucio.value = true
    clearTimeout(temporizador)
    temporizador = window.setTimeout(() => guardar(), 1200)
  }, { deep: true })

  function marcarSucio() { sucio.value = true }

  // --- Operaciones sobre pasos ---------------------------------------------

  function paso(nombre: string): Paso | undefined {
    return config.value?.flujo?.pasos?.[nombre]
  }

  function guardarPaso(nombre: string, p: Paso) {
    if (!config.value?.flujo) return
    config.value.flujo.pasos = { ...config.value.flujo.pasos, [nombre]: p }
  }

  function crearPaso(nombre: string): string | null {
    if (!config.value?.flujo) return 'este guion no tiene flujo'
    if (config.value.flujo.pasos[nombre]) return `ya existe un paso llamado "${nombre}"`
    if (!/^[a-z0-9_]+$/.test(nombre)) {
      return 'usa solo minúsculas, números y guión bajo'
    }
    config.value.flujo.pasos = { ...config.value.flujo.pasos, [nombre]: { decir: '' } }
    return null
  }

  // borrarPaso avisa de quien lo apuntaba en vez de dejar saltos rotos en
  // silencio. La validacion lo detectaria igual, pero decirlo en el momento
  // ahorra buscar despues por que aparecio un problema nuevo.
  function borrarPaso(nombre: string): string[] {
    if (!config.value?.flujo) return []
    const huerfanos: string[] = []
    for (const [otro, p] of Object.entries(config.value.flujo.pasos)) {
      if (otro === nombre) continue
      // Con saltosDe y no con una lista propia: esta era la segunda copia de
      // "qué cuenta como salto", y se quedó atrás cuando aparecieron `cuando` y
      // `delegar`. Borrar el paso al que se delega no avisaba de que alguien lo
      // apuntaba.
      if (saltosDe(p).some(s => s.destino === nombre)) huerfanos.push(otro)
    }
    const { [nombre]: _, ...resto } = config.value.flujo.pasos
    config.value.flujo.pasos = resto
    return huerfanos
  }

  function renombrarPaso(viejo: string, nuevo: string): string | null {
    if (!config.value?.flujo) return 'este guion no tiene flujo'
    if (viejo === nuevo) return null
    if (config.value.flujo.pasos[nuevo]) return `ya existe un paso llamado "${nuevo}"`

    const pasos = { ...config.value.flujo.pasos }
    pasos[nuevo] = pasos[viejo]
    delete pasos[viejo]

    // Reapuntar todo lo que saltaba al nombre viejo. Sin esto, renombrar un paso
    // rompe el guion en silencio — que es exactamente el rastro que dejan los
    // pasos inalcanzables que aparecen en las validaciones.
    for (const p of Object.values(pasos)) {
      if (p.siguiente === viejo) p.siguiente = nuevo
      if (p.si_no_entiende === viejo) p.si_no_entiende = nuevo
      if (p.ok === viejo) p.ok = nuevo
      for (const mapa of [p.opciones, p.error, p.bloqueo]) {
        if (!mapa) continue
        for (const k of Object.keys(mapa)) if (mapa[k] === viejo) mapa[k] = nuevo
      }
      // Las ramas por contenido y los destinos de una delegación también
      // apuntan. Faltaban: renombrar el paso al que se delega dejaba el guion
      // roto en silencio, que es justo lo que esta función existe para evitar.
      for (const c of p.cuando ?? []) if (c.ir === viejo) c.ir = nuevo
      if (p.delegar?.volver === viejo) p.delegar.volver = nuevo
      if (p.delegar?.respaldo === viejo) p.delegar.respaldo = nuevo
    }
    if (config.value.flujo.inicio === viejo) config.value.flujo.inicio = nuevo

    config.value.flujo.pasos = pasos
    return null
  }

  // --- Plantillas -----------------------------------------------------------

  function guardarPlantilla(nombre: string, texto: string) {
    if (!config.value) return
    config.value.plantillas = { ...config.value.plantillas, [nombre]: texto }
    anotarEnCapa(nombre, texto)
  }

  // --- Capas ---------------------------------------------------------------
  //
  // De donde sale cada mensaje: de este cliente o de lo que comparte con los
  // demas de su vertical. Es informacion que hoy no existe en pantalla, y sin
  // ella cambiar un texto compartido parte la vertical en dos sin que nadie lo
  // decida.

  async function cargarCapas() {
    capasPlantilla.value = {}
    enCapas.value = false
    try {
      const r = await api.capas(tenant.value, 'plantillas')
      const m: Record<string, ClaveDelGuion> = {}
      for (const k of r.claves) m[nombreDe(k.clave)] = k
      capasPlantilla.value = m
      enCapas.value = true
    } catch {
      // 409 mientras este cliente no este en capas. No es un fallo que deba
      // romper la pantalla: sin capas se sigue editando como siempre.
    }
  }

  /** Devuelve un mensaje al texto compartido. Es un borrado de la fila propia,
   *  no una copia del texto de la vertical: copiado, este cliente dejaria de
   *  seguir a la vertical el dia que la vertical cambie. */
  async function heredarPlantilla(nombre: string) {
    const k = capasPlantilla.value[nombre]
    if (!k) return
    await api.heredarClave(tenant.value, k.clave)
    // El servidor rehace el borrador al componer, asi que hay que releerlo:
    // el de aqui todavia tiene el texto viejo.
    await abrir(tenant.value)
  }

  /** Copia a este cliente el texto que dice la plataforma, para poder editarlo.
   *
   *  Escribe la fila YA en vez de dejarla al retardo de `anotarEnCapa`. Aquel
   *  retardo existe para no mandar una peticion por tecla, y aqui no se teclea:
   *  se pulsa un boton una vez. Y sobre todo, mientras la fila no exista
   *  `capasPlantilla` no la conoce, asi que el boton de deshacer de al lado se
   *  quedaria sin clave que borrar y no haria nada — sin decirlo. */
  async function escribirMensajeDePlataforma(nombre: string, texto: string) {
    if (!enCapas.value) {
      // Cliente que todavia no esta en capas: se queda en el borrador y lo
      // sube el autoguardado, como cualquier otro mensaje suyo.
      guardarPlantilla(nombre, texto)
      return
    }
    await fijarClave(ruta('plantillas', nombre), texto)
  }

  /** Lo contrario: ese mensaje vuelve a decir lo que dice la plataforma. */
  async function volverAlMensajeDePlataforma(nombre: string) {
    if (!enCapas.value) {
      borrarPlantilla(nombre)
      return
    }
    await heredarPlantilla(nombre)
  }

  // Lo que se escribe en un mensaje viaja tambien a la capa del cliente.
  //
  // Sin esto, el texto quedaria solo en el borrador y la siguiente composicion
  // lo borraria sin avisar — el fallo que no se nota hasta que un cliente ve el
  // mensaje viejo. Va con retardo por la misma razon que el autoguardado: una
  // peticion por tecla no ayuda a nadie.
  const temporizadores: Record<string, number> = {}
  function anotarEnCapa(nombre: string, texto: string) {
    if (!enCapas.value) return
    const clave = capasPlantilla.value[nombre]?.clave ?? ruta('plantillas', nombre)
    window.clearTimeout(temporizadores[nombre])
    temporizadores[nombre] = window.setTimeout(async () => {
      try {
        await api.fijarClave(tenant.value, clave, texto)
        const k = capasPlantilla.value[nombre]
        if (k) capasPlantilla.value = { ...capasPlantilla.value, [nombre]: { ...k, propia: true, capa: 'tenant' } }
      } catch (e) {
        error.value = e instanceof Error ? e.message : String(e)
      }
    }, 1200)
  }

  // --- Composicion: el guion ENTERO con el nivel de cada clave --------------
  //
  // Lo de arriba pide `?bajo=plantillas` porque la vista de mensajes solo
  // enseña mensajes. Esto pide el guion entero, que es la pregunta que no tenia
  // pantalla: «¿que tiene ESTE cliente que no tengan los demas de su vertical?».
  //
  // Va aparte y no reutiliza `capasPlantilla` a proposito. Aquella esta indexada
  // por NOMBRE de plantilla porque es lo que la vista de mensajes tiene a mano;
  // aqui la unidad es la CLAVE completa —`pasos.corporativo_nombre.omitir_si_conocido`—
  // y no hay nombre corto que la identifique. Compartir el mapa obligaria a que
  // una de las dos vistas dedujera la clave de la otra, que es justo el tipo de
  // deduccion que se rompe en silencio cuando alguien añade una rama.

  const composicion = ref<ClaveDelGuion[]>([])
  const basesDelGuion = ref<string[]>([])
  const cargandoComposicion = ref(false)

  // Distinto de `enCapas`: aquel dice si la CARGA de mensajes funciono. Este
  // distingue el 409 —este cliente todavia no tiene su guion en capas— de un
  // fallo de verdad, porque en pantalla son cosas opuestas: uno se explica y el
  // otro se corrige.
  const fueraDeCapas = ref(false)

  async function cargarComposicion() {
    cargandoComposicion.value = true
    fueraDeCapas.value = false
    try {
      const r = await api.capas(tenant.value)
      composicion.value = r.claves
      basesDelGuion.value = r.bases ?? []
    } catch (e) {
      composicion.value = []
      basesDelGuion.value = []
      if (e instanceof ErrorAPI && e.esConflicto) {
        // No es un error: es un cliente que todavia no esta en capas. Se dice,
        // porque una lista vacia sin explicacion se lee como una pantalla rota.
        fueraDeCapas.value = true
      } else {
        error.value = e instanceof Error ? e.message : String(e)
      }
    } finally {
      cargandoComposicion.value = false
    }
  }

  /** Fija una clave cualquiera para este cliente. Sin retardo, al contrario que
   *  `anotarEnCapa`: alli se escribe tecleando y una peticion por tecla no
   *  ayuda a nadie; aqui se pulsa un boton una vez. */
  async function fijarClave(clave: string, valor: string, crudo = false) {
    await api.fijarClave(tenant.value, clave, valor, crudo)
    // Se relee el borrador ademas de la composicion: el servidor lo rehace al
    // componer, y el de aqui todavia tiene el valor viejo. Sin esto, la pestaña
    // de Pasos seguiria enseñando lo anterior hasta que alguien recargara.
    await abrir(tenant.value)
    await cargarComposicion()
  }

  /** Devuelve una clave a lo compartido. Borra la fila propia; no copia el
   *  texto de la vertical, para que vuelva a seguirla cuando la vertical cambie. */
  async function heredarClave(clave: string) {
    await api.heredarClave(tenant.value, clave)
    await abrir(tenant.value)
    await cargarComposicion()
  }

  function borrarPlantilla(nombre: string) {
    if (!config.value?.plantillas) return
    const { [nombre]: _, ...resto } = config.value.plantillas
    config.value.plantillas = resto
  }

  // --- Versiones ------------------------------------------------------------

  async function crearVersion(nota: string) {
    if (sucio.value) await guardar()
    const r = await api.crearVersion(tenant.value, nota)
    await abrir(tenant.value)
    return r.version
  }

  async function descartar() {
    await api.descartarBorrador(tenant.value)
    await abrir(tenant.value)
  }

  return {
    tenant, config, baseVersion, catalogo,
    cargando, guardando, sucio, problemas, error,
    pasos, nombresPasos, plantillas, capacidadesDelTenant,
    abrir, validar, guardar, marcarSucio,
    paso, guardarPaso, crearPaso, borrarPaso, renombrarPaso,
    guardarPlantilla, borrarPlantilla,
    mensajesPlataforma, escribirMensajeDePlataforma, volverAlMensajeDePlataforma,
    capasPlantilla, enCapas, cargarCapas, heredarPlantilla,
    composicion, basesDelGuion, cargandoComposicion, fueraDeCapas,
    cargarComposicion, fijarClave, heredarClave,
    crearVersion, descartar,
  }
})

export const usarSesion = defineStore('sesion', () => {
  const usuario = ref<Awaited<ReturnType<typeof api.yo>> | null>(null)
  const entrando = ref(false)

  const autenticado = computed(() => usuario.value !== null)

  // botia-adm publica lo COMPARTIDO: el catálogo y las capas de las que
  // heredan varios clientes. La línea que lo separa de botia-pub no es la
  // dificultad, es el radio.
  const puedeAdmin = computed(() => usuario.value?.permisos?.includes('botia-adm') ?? false)

  // Y IMPLICA a los otros dos, igual que en el servidor (ver Usuario.Puede).
  // Sin esta implicación aquí, a quien solo tenga botia-adm la consola se le
  // vería a medias: podría publicar el catálogo y no editar un texto, que es
  // justo el reparto que no tiene sentido.
  const puedeEditar = computed(() =>
    puedeAdmin.value || (usuario.value?.permisos?.includes('botia-edit') ?? false))
  const puedePublicar = computed(() =>
    puedeAdmin.value || (usuario.value?.permisos?.includes('botia-pub') ?? false))

  // Espejo de PermisoLeerConfig (auth.go): editar O líder, SIN bajar a
  // PermisoVer -eso incluiría a los 141 asesores con botia-ver, que no deben
  // ver el catálogo ni las capas compartidas-. Es lo que gobierna el catálogo
  // y las capas compartidas: se leen con esta condición y se publican con
  // botia-adm, aparte (ver Catalogo.vue y Capas.vue).
  const puedeLeerConfig = computed(() =>
    puedeEditar.value || esLider(usuario.value?.permisos ?? []))

  // puedePublicarEn es para las pantallas DE UN CLIENTE (Inteligencia,
  // Versiones, Operación), que necesitan saber CUÁL cliente para decidir: un
  // líder no lleva botia-pub GLOBAL -es lo que cierra las rutas globales de
  // plataforma, ver PermisoLider en auth.go- pero SÍ puede publicar DENTRO de
  // su alcance.
  //
  // NO SE TOCA puedePublicar de arriba: las pantallas GLOBALES (Plataforma,
  // precios, proveedores, credenciales de plataforma) siguen exigiendo el
  // permiso global a secas, y ahí un líder debe seguir sin botones -la API le
  // responde 403 igual que a cualquiera sin botia-pub-.
  //
  // Sin esto, un líder tendría el permiso por la API (Task 4, Task 9) y la
  // consola le seguiría escondiendo los botones: la funcionalidad existiría
  // solo para quien supiera usar curl. Ver Ruling 5 del plan de permisos por
  // tenant, y el espejo de Usuario.PuedeEn en tipos/alcance.ts.
  //
  // NO HAY un puedeEditarEn hermano, y no es un olvido: exigirAlcance (Go)
  // exige PuedeEn(PermisoEditar, id) para CUALQUIER sub-ruta de
  // /api/tenants/{id}/*, así que quien llega a cargar la pantalla de un
  // cliente -cualquier pestaña- YA tiene editar-en-ese-cliente por
  // construcción; condicionar un botón de edición con un `puedeEditarEn` ahí
  // sería una comprobación que siempre da true. Existió un `puedeEditarEn`
  // (Task 11) sin ningún sitio que lo llamara -lo cazó la segunda lectura
  // adversarial del 12 de septiembre de 2026- y se quitó por eso: código sin
  // uso invita a que alguien confíe en él para algo que no cubre.
  function puedePublicarEn(tenantId: string): boolean {
    return puedeEn(usuario.value?.permisos ?? [], usuario.value?.alcance, PERMISO_PUBLICAR, tenantId)
  }

  async function recuperar(): Promise<boolean> {
    entrando.value = true
    try {
      usuario.value = await api.yo()
      return true
    } catch (e) {
      if (e instanceof ErrorAPI && e.esSesionCaida) usuario.value = null
      return false
    } finally {
      entrando.value = false
    }
  }

  function salir() {
    usuario.value = null
  }

  return {
    usuario, entrando, autenticado, puedeAdmin, puedeEditar, puedePublicar, puedeLeerConfig,
    puedePublicarEn, recuperar, salir,
  }
})
